import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, Link, useNavigate, useBlocker } from 'react-router-dom';
import { api } from '@/api/client';
import {
  beaconUploadFailed,
  presignMissingUploads,
  reportUploadFailed,
  sendUploadHeartbeat,
  shouldUseMultipartUpload,
  submitPipelineJob,
  uploadAudioToS3,
  uploadAudioMultipartToS3,
  startPipeline,
} from '@/api/pipeline';
import type { AudioTrackInfo, AudioTrackRole, Day1Variant, ParticipantInfo } from '@/types/pipeline';
import { mapWithConcurrency } from '@/utils/async';
import { guessUploadContentType } from '@/utils/uploadContentType';

const DAY_OPTIONS = [
  { value: 'Day1', label: 'Day 1' },
  { value: 'Day2', label: 'Day 2' },
  { value: 'Day3', label: 'Day 3' },
  // 旧互換: UI 上の "DayX（4回目以降）" は API 上 Day4 として送り、backend で DayX に寄せる。
  { value: 'Day4', label: 'Day X（4回目以降）' },
  { value: 'ProgressMTG', label: '進捗会議' },
  { value: 'PreMTG', label: '事前MTG' },
  { value: 'PostMTG', label: '事後MTG' },
];

type TrackMode = 'single' | 'multi';

interface FileWithDuration {
  file: File;
  duration: number; // 秒
}

interface ClientFeedbacker {
  user_id: string;
  user_name: string;
  email?: string | null;
  assignment_source?: string | null;
}

// GET /clients/{id} はクライアントユーザーに対して個人情報（email 等）を含む
// `feedbackers` を空で返し、担当者名だけを `assigned_feedbacker_names` で返す。
// 確認画面で「未設定」と誤表示しないよう、名前のみの一覧にフォールバックする。
function toClientFeedbackers(data: unknown): ClientFeedbacker[] {
  const record = (data ?? {}) as {
    feedbackers?: unknown;
    assigned_feedbacker_names?: unknown;
  };
  if (Array.isArray(record.feedbackers) && record.feedbackers.length > 0) {
    return record.feedbackers as ClientFeedbacker[];
  }
  if (Array.isArray(record.assigned_feedbacker_names)) {
    return record.assigned_feedbacker_names.map((name, index) => ({
      user_id: `assigned-name-${index}`,
      user_name: String(name),
    }));
  }
  return [];
}

type UploadItem = { file: File; role: AudioTrackRole; uploadFilename: string };
type UploadPhase = 'uploading' | 'starting';

interface FailedUpload {
  role: AudioTrackRole;
  filename: string;
  file: File;
  reason: string;
}

interface UploadTarget {
  key: string;
  role: AudioTrackRole;
  filename: string;
  size: number;
}

interface UploadOrderWarning {
  key: string;
  message: string;
}

const UPLOAD_NAVIGATION_WARNING =
  'アップロード中です。移動するとアップロードが中断される可能性があります。移動しますか？';
const S3_UPLOAD_CONCURRENCY = 1;
const S3_UPLOAD_MAX_ATTEMPTS = 2;
const S3_UPLOAD_STALL_TIMEOUT_MS = 180_000;

/** ブラウザの Audio API で音声ファイルの長さ（秒）を取得 */
function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(isFinite(audio.duration) ? audio.duration : 0);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    audio.src = url;
  });
}

function formatDuration(seconds: number): string {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function assignUploadFilenames(items: Array<Omit<UploadItem, 'uploadFilename'>>): UploadItem[] {
  const totals = new Map<string, number>();
  for (const item of items) {
    const key = `${item.role}\0${item.file.name}`;
    totals.set(key, (totals.get(key) ?? 0) + 1);
  }
  return items.map((item, index) => {
    const key = `${item.role}\0${item.file.name}`;
    const duplicateInRole = (totals.get(key) ?? 0) > 1;
    return {
      ...item,
      uploadFilename: duplicateInRole
        ? `${String(index + 1).padStart(3, '0')}_${item.file.name}`
        : item.file.name,
    };
  });
}

function naturalCompareFileName(a: string, b: string) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function orderSignature(files: FileWithDuration[], order: number[]) {
  return order.map((index) => files[index]?.file.name ?? '').join('\0');
}

function buildFileOrderWarnings(label: string, files: FileWithDuration[]): UploadOrderWarning[] {
  if (files.length < 2) return [];

  const originalOrder = files.map((_, index) => index);
  const warnings: UploadOrderWarning[] = [];
  const duplicateNames = new Set<string>();
  const seenNames = new Set<string>();

  for (const file of files) {
    const normalizedName = file.file.name.toLowerCase();
    if (seenNames.has(normalizedName)) duplicateNames.add(file.file.name);
    seenNames.add(normalizedName);
  }

  if (duplicateNames.size > 0) {
    warnings.push({
      key: `${label}-duplicate-names`,
      message: `${label} に同名ファイルがあります。対象ファイルおよび順序は正しいか確認ください。こちらで問題なければこのままアップロードしてください。`,
    });
  }

  const fileNameOrder = [...originalOrder].sort((a, b) => {
    const nameCompare = naturalCompareFileName(files[a]!.file.name, files[b]!.file.name);
    return nameCompare || a - b;
  });
  if (orderSignature(files, fileNameOrder) !== orderSignature(files, originalOrder)) {
    warnings.push({
      key: `${label}-filename-order`,
      message: `${label} の選択順がファイル名の自然順と異なります。対象ファイルおよび順序は正しいか確認ください。こちらで問題なければこのままアップロードしてください。`,
    });
  }

  const modifiedOrder = [...originalOrder].sort((a, b) => {
    const modifiedCompare = files[a]!.file.lastModified - files[b]!.file.lastModified;
    return modifiedCompare || a - b;
  });
  const modifiedTimes = new Set(files.map((file) => file.file.lastModified));
  if (
    modifiedTimes.size > 1 &&
    orderSignature(files, modifiedOrder) !== orderSignature(files, originalOrder)
  ) {
    warnings.push({
      key: `${label}-modified-order`,
      message: `${label} の選択順がファイル更新日時の古い順と異なります。対象ファイルおよび順序は正しいか確認ください。こちらで問題なければこのままアップロードしてください。`,
    });
  }

  return warnings;
}

/** S2: 新規セッション追加画面 */
export function UploadPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const mixedInputRef = useRef<HTMLInputElement>(null);
  const tr1InputRef = useRef<HTMLInputElement>(null);
  const trlrInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const clientDetailsRequestRef = useRef(0);
  const allowUploadNavigationRef = useRef(false);
  const uploadCancelledRef = useRef(false);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);

  const [trackMode, setTrackMode] = useState<TrackMode>('single');
  const [mixedFiles, setMixedFiles] = useState<FileWithDuration[]>([]);
  const [tr1Files, setTr1Files] = useState<FileWithDuration[]>([]);
  const [trlrFiles, setTrlrFiles] = useState<FileWithDuration[]>([]);

  const [dayType, setDayType] = useState('Day1');
  const [day1Variant, setDay1Variant] = useState<Day1Variant>('first');
  const [sessionDate, setSessionDate] = useState(() =>
    new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo' }).format(new Date()),
  );
  const [participants, setParticipants] = useState<ParticipantInfo[]>([
    { name: '', role: 'facilitator' },
  ]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadTargets, setUploadTargets] = useState<UploadTarget[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [failedUploadJobId, setFailedUploadJobId] = useState<string | null>(null);
  const [failedUploads, setFailedUploads] = useState<FailedUpload[]>([]);
  const [currentUploadJobId, setCurrentUploadJobId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string>('');
  const [clientNameLoadFailed, setClientNameLoadFailed] = useState(false);
  const [clientFeedbackers, setClientFeedbackers] = useState<ClientFeedbacker[] | null>(null);
  const [clientAgreementUploaded, setClientAgreementUploaded] = useState<boolean | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // セッション実施日は pipeline submit に session_date として送信される（A-26）。

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      uploadAbortControllerRef.current?.abort();
    };
  }, []);

  const fetchClientDetails = useCallback(
    async ({ resetFeedbackers = false }: { resetFeedbackers?: boolean } = {}) => {
      if (!clientId) return false;
      const requestId = clientDetailsRequestRef.current + 1;
      clientDetailsRequestRef.current = requestId;
      setClientNameLoadFailed(false);
      if (resetFeedbackers) setClientFeedbackers(null);
      setClientAgreementUploaded(null);

      try {
        const r = await api.get(`/clients/${clientId}`);
        if (!mountedRef.current || clientDetailsRequestRef.current !== requestId) return false;
        setClientName(String(r.data?.name ?? ''));
        setClientFeedbackers(toClientFeedbackers(r.data));
        setClientAgreementUploaded(r.data?.information_disclosure_agreement_uploaded === true);
        return true;
      } catch {
        if (!mountedRef.current || clientDetailsRequestRef.current !== requestId) return false;
        setClientNameLoadFailed(true);
        setClientFeedbackers(null);
        setClientAgreementUploaded(null);
        return false;
      }
    },
    [clientId],
  );

  useEffect(() => {
    void fetchClientDetails({ resetFeedbackers: true });
  }, [fetchClientDetails]);

  useEffect(() => {
    if (!uploading) return;
    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentUploadJobId) {
        beaconUploadFailed(currentUploadJobId, {
          reason: 'browser unload during audio upload',
          missing_files: uploadTargets.map((target) => ({
            role: target.role,
            filename: target.filename,
          })),
        });
      }
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [uploading, currentUploadJobId, uploadTargets]);

  useEffect(() => {
    if (!uploading || !currentUploadJobId) return;
    void sendUploadHeartbeat(currentUploadJobId).catch(() => undefined);
    const timer = window.setInterval(() => {
      void sendUploadHeartbeat(currentUploadJobId).catch(() => undefined);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [uploading, currentUploadJobId]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!uploading || allowUploadNavigationRef.current) return false;
    return (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash
    );
  });

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm(UPLOAD_NAVIGATION_WARNING)) {
      uploadCancelledRef.current = true;
      uploadAbortControllerRef.current?.abort();
      if (currentUploadJobId) {
        beaconUploadFailed(currentUploadJobId, {
          reason: 'in-app navigation during audio upload',
          missing_files: uploadTargets.map((target) => ({
            role: target.role,
            filename: target.filename,
          })),
        });
      }
      setCurrentUploadJobId(null);
      setUploading(false);
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, currentUploadJobId, uploadTargets]);

  const isAudioFile = (file: File) =>
    file.type.startsWith('audio/') ||
    file.type.startsWith('video/') ||
    /\.(wav|mp3|m4a|flac|mp4|webm|mov|m4v|ogg)$/i.test(file.name);

  const errorText = (err: unknown, fallback: string) => {
    const detail =
      (err as { response?: { data?: { detail?: string | { message?: string }; message?: string } } })
        .response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    return (
      (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
      (err instanceof Error ? err.message : fallback)
    );
  };

  const addFiles = useCallback(
    async (
      incoming: File[],
      setter: React.Dispatch<React.SetStateAction<FileWithDuration[]>>,
    ) => {
      const valid = incoming.filter(isAudioFile);
      if (valid.length === 0) return;
      const withMeta: FileWithDuration[] = [];
      for (const file of valid) {
        const duration = await getAudioDuration(file);
        withMeta.push({ file, duration });
      }
      setter((prev) => [...prev, ...withMeta]);
    },
    [],
  );

  const handleMixedDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    void addFiles(Array.from(e.dataTransfer.files), setMixedFiles);
  };

  const addParticipant = () => {
    setParticipants((prev) => [...prev, { name: '', role: 'participant' }]);
  };

  const removeParticipant = (index: number) => {
    setParticipants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateParticipant = (index: number, field: 'name' | 'role', value: string) => {
    setParticipants((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  // ── 集計・バリデーション ──
  const tr1Total = tr1Files.reduce((a, f) => a + f.duration, 0);
  const trlrTotal = trlrFiles.reduce((a, f) => a + f.duration, 0);
  const durationDiff = Math.abs(tr1Total - trlrTotal);
  const hasDurationMismatch =
    trackMode === 'multi' &&
    tr1Files.length > 0 &&
    trlrFiles.length > 0 &&
    durationDiff > 5;
  const uploadOrderWarnings =
    trackMode === 'single'
      ? buildFileOrderWarnings('単一ファイル', mixedFiles)
      : [
          ...buildFileOrderWarnings('Tr1', tr1Files),
          ...buildFileOrderWarnings('TrLR', trlrFiles),
        ];
  const confirmWarnings = [
    ...(hasDurationMismatch
      ? [{
          key: 'duration-mismatch',
          message: `Tr1 と TrLR の合計時間が一致しません。差分 ${formatDuration(durationDiff)}。ファイルの選択を確認してください。`,
        }]
      : []),
    ...uploadOrderWarnings,
  ];

  const canSubmit =
    (trackMode === 'single' ? mixedFiles.length > 0 : trlrFiles.length > 0) &&
    clientAgreementUploaded === true;

  const selectedDayLabel = DAY_OPTIONS.find((opt) => opt.value === dayType)?.label ?? dayType;
  const uploadProgressRows = uploadTargets.map((target) => ({
    ...target,
    percent: Math.min(100, Math.max(0, Math.round(uploadProgress[target.key] ?? 0))),
  }));
  const uploadCompletedCount = uploadProgressRows.filter((row) => row.percent >= 100).length;
  const totalUploadBytes = uploadProgressRows.reduce((sum, row) => sum + row.size, 0);
  const uploadedBytes = uploadProgressRows.reduce(
    (sum, row) => sum + (row.size * row.percent) / 100,
    0,
  );
  const overallUploadProgress = uploadProgressRows.length > 0
    ? Math.round((uploadedBytes / Math.max(totalUploadBytes, 1)) * 100)
    : 0;
  const shouldShowUploadProgress =
    uploadProgressRows.length > 0 && (uploading || failedUploadJobId !== null);
  const uploadingButtonLabel =
    uploading && overallUploadProgress > 0
      ? `アップロード中 ${overallUploadProgress}%`
      : 'アップロード中...';

  const buildUploadTargets = (items: UploadItem[]) =>
    items.map((it) => ({
      key: `${it.role}/${it.uploadFilename}`,
      role: it.role,
      filename: it.uploadFilename,
      size: it.file.size,
    }));

  const setFileUploadProgress = (key: string, percent: number) => {
    const clamped = Math.min(100, Math.max(0, Math.round(percent)));
    setUploadProgress((prev) => ({ ...prev, [key]: clamped }));
  };

  const reportUploadFailure = async (
    jobId: string,
    reason: string,
    files: Array<{ role: AudioTrackRole | string; filename: string }>,
  ) => {
    await reportUploadFailed(jobId, {
      reason,
      missing_files: files.map((file) => ({ role: file.role, filename: file.filename })),
    }).catch(() => undefined);
  };

  const createUploadHeartbeatReporter = (jobId: string) => {
    let lastSentAt = 0;
    return (force = false) => {
      const now = Date.now();
      if (!force && now - lastSentAt < 10_000) return;
      lastSentAt = now;
      void sendUploadHeartbeat(jobId).catch(() => undefined);
    };
  };

  const uploadFileWithRetry = async (
    jobId: string,
    role: AudioTrackRole,
    filename: string,
    uploadUrl: string,
    file: File,
    onProgress: (percent: number) => void,
    contentType?: string,
    signal?: AbortSignal,
  ) => {
    if (shouldUseMultipartUpload(file)) {
      await uploadAudioMultipartToS3(
        jobId,
        {
          role,
          filename,
          content_type: contentType || guessUploadContentType(file),
          file_size: file.size,
        },
        file,
        {
          onProgress,
          stallTimeoutMs: S3_UPLOAD_STALL_TIMEOUT_MS,
          signal,
        },
      );
      return;
    }
    let lastError: unknown;
    for (let attempt = 1; attempt <= S3_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
      try {
        await uploadAudioToS3(uploadUrl, file, onProgress, contentType, {
          stallTimeoutMs: S3_UPLOAD_STALL_TIMEOUT_MS,
          signal,
        });
        return;
      } catch (err) {
        lastError = err;
        if (attempt < S3_UPLOAD_MAX_ATTEMPTS) {
          await new Promise((resolve) => window.setTimeout(resolve, attempt * 1000));
        }
      }
    }
    throw lastError;
  };

  const buildUploadItems = (): UploadItem[] => {
    // role キーは backend (pipeline_service.py:501) が小文字で集合判定するため
    // 送信時は必ず lowercase に揃える。
    const items: Array<Omit<UploadItem, 'uploadFilename'>> = [];
    if (trackMode === 'single') {
      for (const fd of mixedFiles) items.push({ file: fd.file, role: 'mixed' });
    } else {
      for (const fd of tr1Files) items.push({ file: fd.file, role: 'tr1' });
      for (const fd of trlrFiles) items.push({ file: fd.file, role: 'trlr' as AudioTrackRole });
    }
    return assignUploadFilenames(items);
  };

  const buildParticipantsRoster = () =>
    participants
      .filter((p) => p.name.trim().length > 0)
      .map((p) => ({ name: p.name.trim(), role: p.role }));

  const handleConfirm = () => {
    if (clientAgreementUploaded !== true) {
      setErrorMessage('セッション追加には、クライアントと交わした情報開示の合意書PDFのアップロードが必要です');
      return;
    }
    if (!canSubmit || uploading) return;
    setErrorMessage('');
    setIsConfirming(true);
    void fetchClientDetails({ resetFeedbackers: true });
  };

  const handleUpload = async () => {
    if (clientFeedbackers === null) return;

    const items = buildUploadItems();
    if (items.length === 0) return;

    setUploading(true);
    uploadCancelledRef.current = false;
    allowUploadNavigationRef.current = false;
    setErrorMessage('');
    setFailedUploadJobId(null);
    setFailedUploads([]);
    setUploadTargets(buildUploadTargets(items));
    setUploadProgress({});
    let keepStatusMessage = false;
    let submittedJobId: string | null = null;
    let submittedItems: UploadItem[] = [];
    let uploadPhase: UploadPhase = 'uploading';
    const abortController = new AbortController();
    uploadAbortControllerRef.current = abortController;

    try {
      // 1. tracks 構築
      const tracks: AudioTrackInfo[] = items.map((it, index) => ({
        filename: it.uploadFilename,
        role: it.role,
        content_type: guessUploadContentType(it.file),
        order_index: index,
      }));

      // 2. participants_roster をフィルタ（name 空除外）
      const roster = buildParticipantsRoster();

      // 3. submit
      if (!clientId) {
        throw new Error('クライアント ID が URL に含まれていません');
      }
      setStatusMessage('ジョブ投入中...');
      const isDay1 = dayType === 'Day1';
      const submitResp = await submitPipelineJob({
        client_id: clientId,
        day: dayType,
        cycle: isDay1 && day1Variant === 'repeat' ? 2 : 1,
        ...(isDay1 ? { day1_variant: day1Variant } : {}),
        tracks,
        use_rag: true,
        session_date: sessionDate,
        ...(roster.length > 0
          ? {
              participants_roster: roster,
              participants: roster.map((r) => r.name),
            }
          : {}),
      });

      const jobId = submitResp.job_id;
      submittedJobId = jobId;
      submittedItems = items;
      setCurrentUploadJobId(jobId);
      await sendUploadHeartbeat(jobId).catch(() => undefined);
      const reportUploadHeartbeat = createUploadHeartbeatReporter(jobId);

      // 4. presigned PUT を並列数を絞って実行
      setStatusMessage('音声ファイルをアップロード中...');
      const uploadResults = await mapWithConcurrency(items, S3_UPLOAD_CONCURRENCY, async (it) => {
        const url = submitResp.upload_urls.find(
          (u) => u.role === it.role && u.filename === it.uploadFilename,
        );
        const key = `${it.role}/${it.uploadFilename}`;
        try {
          if (!url) {
            throw new Error(
              `presigned URL が見つかりません: role=${it.role}, filename=${it.uploadFilename}`,
            );
          }
          await uploadFileWithRetry(
            jobId,
            it.role,
            it.uploadFilename,
            url.upload_url,
            it.file,
            (pct) => {
              setFileUploadProgress(key, pct);
              reportUploadHeartbeat();
            },
            url.content_type ?? guessUploadContentType(it.file),
            abortController.signal,
          );
          reportUploadHeartbeat(true);
          setFileUploadProgress(key, 100);
          return null;
        } catch (err) {
          return {
            role: it.role,
            filename: it.uploadFilename,
            file: it.file,
            reason: errorText(err, 'アップロードに失敗しました'),
          } satisfies FailedUpload;
        }
      });
      const failed = uploadResults.filter((r): r is FailedUpload => r !== null);
      if (uploadCancelledRef.current) {
        keepStatusMessage = true;
        return;
      }
      if (failed.length > 0) {
        setCurrentUploadJobId(null);
        await reportUploadFailure(
          jobId,
          'audio upload PUT failed',
          failed.map((it) => ({ role: it.role, filename: it.filename })),
        );
        setFailedUploadJobId(jobId);
        setFailedUploads(failed);
        setErrorMessage('一部のファイルをアップロードできませんでした。失敗したファイルだけ再アップロードできます。');
        setStatusMessage('アップロード未完了');
        keepStatusMessage = true;
        return;
      }

      // 5. パイプライン起動
      if (uploadCancelledRef.current) {
        keepStatusMessage = true;
        return;
      }
      uploadPhase = 'starting';
      setStatusMessage('処理を開始します...');
      await startPipeline(jobId);
      if (uploadCancelledRef.current) {
        keepStatusMessage = true;
        return;
      }
      setCurrentUploadJobId(null);

      // 6. 処理状況画面へ遷移（pipeline ジョブの場合は jobId をクエリで渡す）
      allowUploadNavigationRef.current = true;
      navigate(`/sessions/${jobId}/status?jobId=${jobId}`);
    } catch (err) {
      const detail = (err as {
        response?: {
          data?: {
            detail?: {
              code?: string;
              missing_files?: Array<{ role: string; filename: string }>;
            } | string;
          };
        };
      }).response?.data?.detail;
      if (submittedJobId && typeof detail === 'object' && detail.code === 'upload.missing_files') {
        const missingFiles = detail.missing_files ?? [];
        const missingUploads = missingFiles
          .map((missing) => {
            const item = submittedItems.find(
              (it) => it.role === missing.role && it.uploadFilename === missing.filename,
            );
            if (!item) return null;
            return {
              role: item.role,
              filename: item.uploadFilename,
              file: item.file,
              reason: 'S3上でファイルを確認できませんでした',
            } satisfies FailedUpload;
          })
          .filter((it): it is FailedUpload => it !== null);
        await reportUploadFailure(
          submittedJobId,
          'pipeline start detected missing audio files',
          missingUploads.map((it) => ({ role: it.role, filename: it.filename })),
        );
        setCurrentUploadJobId(null);
        setFailedUploadJobId(submittedJobId);
        setFailedUploads(missingUploads);
        setErrorMessage('音声ファイルのアップロードが完了していません。失敗したファイルだけ再アップロードできます。');
        setStatusMessage('アップロード未完了');
        keepStatusMessage = true;
        return;
      }
      const msg = errorText(err, 'アップロードに失敗しました');
      setCurrentUploadJobId(null);
      if (submittedJobId && uploadPhase === 'starting') {
        allowUploadNavigationRef.current = true;
        navigate(`/sessions/${submittedJobId}/status?jobId=${submittedJobId}`);
        return;
      }
      if (submittedJobId && uploadPhase === 'uploading') {
        await reportUploadFailure(submittedJobId, msg, submittedItems.map((it) => ({
          role: it.role,
          filename: it.uploadFilename,
        })));
      }
      setErrorMessage(`エラー: ${msg}`);
    } finally {
      setUploading(false);
      if (!keepStatusMessage) setStatusMessage('');
      if (uploadAbortControllerRef.current === abortController) {
        uploadAbortControllerRef.current = null;
      }
    }
  };

  const handleRetryFailedUploads = async () => {
    if (!failedUploadJobId || failedUploads.length === 0) return;

    setUploading(true);
    uploadCancelledRef.current = false;
    setCurrentUploadJobId(failedUploadJobId);
    allowUploadNavigationRef.current = false;
    setErrorMessage('');
    setUploadTargets(buildUploadTargets(failedUploads.map((it) => ({
      file: it.file,
      role: it.role,
      uploadFilename: it.filename,
    }))));
    setUploadProgress({});
    let keepStatusMessage = false;
    let uploadPhase: UploadPhase = 'uploading';
    const abortController = new AbortController();
    uploadAbortControllerRef.current = abortController;

    try {
      setStatusMessage('失敗したファイルの再アップロードURLを取得中...');
      const presignResp = await presignMissingUploads(
        failedUploadJobId,
        failedUploads.map((it) => ({
          role: it.role,
          filename: it.filename,
          content_type: guessUploadContentType(it.file),
        })),
      );

      setStatusMessage('失敗したファイルを再アップロード中...');
      await sendUploadHeartbeat(failedUploadJobId).catch(() => undefined);
      const reportUploadHeartbeat = createUploadHeartbeatReporter(failedUploadJobId);
      const retryResults = await mapWithConcurrency(failedUploads, S3_UPLOAD_CONCURRENCY, async (it) => {
        const url = presignResp.upload_urls.find(
          (u) => u.role === it.role && u.filename === it.filename,
        );
        if (!url) return null;
        const key = `${it.role}/${it.filename}`;
        try {
          await uploadFileWithRetry(
            failedUploadJobId,
            it.role,
            it.filename,
            url.upload_url,
            it.file,
            (pct) => {
              setFileUploadProgress(key, pct);
              reportUploadHeartbeat();
            },
            url.content_type ?? guessUploadContentType(it.file),
            abortController.signal,
          );
          reportUploadHeartbeat(true);
          setFileUploadProgress(key, 100);
          return null;
        } catch (err) {
          return {
            ...it,
            reason: errorText(err, '再アップロードに失敗しました'),
          } satisfies FailedUpload;
        }
      });

      const failedAgain = retryResults.filter((r): r is FailedUpload => r !== null);
      if (uploadCancelledRef.current) {
        keepStatusMessage = true;
        return;
      }
      if (failedAgain.length > 0) {
        setCurrentUploadJobId(null);
        await reportUploadFailure(
          failedUploadJobId,
          'audio re-upload PUT failed',
          failedAgain.map((it) => ({ role: it.role, filename: it.filename })),
        );
        setFailedUploads(failedAgain);
        setErrorMessage('再アップロードに失敗したファイルがあります。もう一度やり直してください。');
        setStatusMessage('アップロード未完了');
        keepStatusMessage = true;
        return;
      }

      uploadPhase = 'starting';
      setStatusMessage('処理を開始します...');
      if (uploadCancelledRef.current) {
        keepStatusMessage = true;
        return;
      }
      await startPipeline(failedUploadJobId);
      if (uploadCancelledRef.current) {
        keepStatusMessage = true;
        return;
      }
      setCurrentUploadJobId(null);
      allowUploadNavigationRef.current = true;
      navigate(`/sessions/${failedUploadJobId}/status?jobId=${failedUploadJobId}`);
    } catch (err) {
      const msg = errorText(err, '再アップロードに失敗しました');
      setCurrentUploadJobId(null);
      if (uploadPhase === 'starting') {
        allowUploadNavigationRef.current = true;
        navigate(`/sessions/${failedUploadJobId}/status?jobId=${failedUploadJobId}`);
        return;
      }
      await reportUploadFailure(
        failedUploadJobId,
        msg,
        failedUploads.map((it) => ({ role: it.role, filename: it.filename })),
      );
      setErrorMessage(`エラー: ${msg}`);
    } finally {
      setUploading(false);
      if (!keepStatusMessage) setStatusMessage('');
      if (uploadAbortControllerRef.current === abortController) {
        uploadAbortControllerRef.current = null;
      }
    }
  };

  const failedUploadRecoveryPanel = failedUploadJobId ? (
    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
      <p className="font-semibold">アップロード未完了のジョブ</p>
      {failedUploads.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {failedUploads.map((it) => (
            <li
              key={`${it.role}/${it.filename}`}
              className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="font-mono text-xs">{it.role}/{it.filename}</span>
              <span className="text-xs text-orange-700">{it.reason}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-orange-700">
          この画面で不足ファイルを特定できませんでした。処理状況画面から不足ファイルを選び直してください。
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {failedUploads.length > 0 && (
          <button
            type="button"
            onClick={handleRetryFailedUploads}
            disabled={uploading}
            className="rounded bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
          >
            失敗したファイルだけ再アップロード
          </button>
        )}
        <Link
          to={`/sessions/${failedUploadJobId}/status?jobId=${failedUploadJobId}`}
          className="rounded border border-orange-300 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100"
        >
          処理状況で確認
        </Link>
      </div>
    </div>
  ) : null;

  return (
    <div>
      <div className="mb-4">
        <Link to={`/clients/${clientId}`} className="text-sm text-blue-600 hover:underline">
          ← クライアント詳細
        </Link>
      </div>

      <div>
        <p className="text-sm text-gray-500">
          クライアント:{' '}
          <span className="font-medium text-gray-700">
            {clientName || (clientNameLoadFailed ? '取得できませんでした' : '読み込み中...')}
          </span>
        </p>
        <h1 className="mt-1 text-xl font-semibold text-gray-900">新規セッション追加</h1>
      </div>

      {clientAgreementUploaded === false && (
        <div className="mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          セッション追加には、クライアント詳細画面で情報開示の合意書PDFをアップロードしてください。
        </div>
      )}

      {isConfirming ? (
        <div className="mt-6 max-w-2xl space-y-6">
          <div>
            <p className="text-sm font-medium text-blue-700">確認ステップ</p>
            <h2 className="mt-1 text-lg font-semibold text-gray-900">登録内容の確認</h2>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <ConfirmRow label="クライアント">
              {clientName || (clientNameLoadFailed ? '取得できませんでした' : '読み込み中...')}
            </ConfirmRow>
            <ConfirmRow label="フィードバッカー">
              <FeedbackerSummaryList
                feedbackers={clientFeedbackers}
                loadFailed={clientNameLoadFailed}
              />
            </ConfirmRow>
            <ConfirmRow label="日付">{sessionDate}</ConfirmRow>
            <ConfirmRow label="会議種別">{selectedDayLabel}</ConfirmRow>
            <ConfirmRow label="トラック構成">
              {trackMode === 'single'
                ? `単一ファイル（${mixedFiles.length}ファイル）`
                : `複数トラック（Tr1: ${tr1Files.length}ファイル / TrLR: ${trlrFiles.length}ファイル）`}
            </ConfirmRow>
            <ConfirmRow label="選択ファイル">
              <div className="space-y-2">
                {trackMode === 'single' ? (
                  <FileSummaryList files={mixedFiles} />
                ) : (
                  <>
                    <div>
                      <p className="text-xs font-semibold text-gray-500">Tr1（コーチ）</p>
                      <FileSummaryList files={tr1Files} emptyLabel="未選択" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500">TrLR（参加者卓上）</p>
                      <FileSummaryList files={trlrFiles} emptyLabel="未選択" />
                    </div>
                  </>
                )}
              </div>
            </ConfirmRow>
            <ConfirmRow label="参加者">
              {buildParticipantsRoster().length > 0 ? (
                <ul className="space-y-1">
                  {buildParticipantsRoster().map((p, i) => (
                    <li key={`${p.name}-${i}`}>
                      {p.name}（{p.role === 'facilitator' ? 'コーチ' : '参加者'}）
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-500">未入力</span>
              )}
            </ConfirmRow>
            <ConfirmRow label="注意事項">
              {confirmWarnings.length > 0 ? (
                <ul className="space-y-2">
                  {confirmWarnings.map((warning) => (
                    <li key={warning.key} className="rounded-md bg-red-50 px-3 py-2 text-red-700">
                      {warning.message}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-500">注意事項はありません</span>
              )}
            </ConfirmRow>
          </div>

          {statusMessage && (
            <div className="rounded-md bg-blue-50 px-4 py-2 text-sm text-blue-700">
              {statusMessage}
            </div>
          )}
          {shouldShowUploadProgress && (
            <UploadProgressPanel
              rows={uploadProgressRows}
              overallPercent={overallUploadProgress}
              completedCount={uploadCompletedCount}
            />
          )}
          {errorMessage && (
            <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">
              {errorMessage}
            </div>
          )}
          {failedUploadRecoveryPanel}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleUpload}
              disabled={!canSubmit || uploading || clientFeedbackers === null}
              className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? uploadingButtonLabel : 'この内容で登録'}
            </button>
            <button
              type="button"
              onClick={() => setIsConfirming(false)}
              disabled={uploading}
              className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              戻る
            </button>
            <button
              type="button"
              onClick={() => setIsConfirming(false)}
              disabled={uploading}
              className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              キャンセル
            </button>
          </div>
        </div>
      ) : (
      <div className="mt-6 max-w-2xl space-y-6">
        {/* Day 種別 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">セッション種別</label>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {DAY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDayType(opt.value)}
                className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                  dayType === opt.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {dayType === 'Day1' && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">Day1区分</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { value: 'first' as Day1Variant, label: '初回' },
                  { value: 'repeat' as Day1Variant, label: '複数回目' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDay1Variant(opt.value)}
                    className={`rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                      day1Variant === opt.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 実施日 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">実施日</label>
          <input
            type="date"
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
            className="mt-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm"
          />
        </div>

        {/* 参加者 */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">参加者</label>
            <button
              type="button"
              onClick={addParticipant}
              className="text-sm text-blue-600 hover:underline"
            >
              + 参加者を追加
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {participants.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={p.role}
                  onChange={(e) => updateParticipant(i, 'role', e.target.value)}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                >
                  <option value="facilitator">コーチ</option>
                  <option value="participant">参加者</option>
                </select>
                <input
                  type="text"
                  placeholder="名前"
                  value={p.name}
                  onChange={(e) => updateParticipant(i, 'name', e.target.value)}
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-sm"
                />
                {participants.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeParticipant(i)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    削除
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 音声ファイル */}
        <div>
          <label className="block text-sm font-medium text-gray-700">音声ファイル</label>
          <p className="mt-1 text-xs text-gray-500">
            単一リミックス済みファイルなら「単一ファイル」、ZOOM H5 等で別撮りした場合は「複数トラック」を選択。
          </p>

          {/* モード切替トグル */}
          <div className="mt-2 flex rounded-lg border border-gray-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => setTrackMode('single')}
              disabled={uploading}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                trackMode === 'single'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              単一ファイル
            </button>
            <button
              type="button"
              onClick={() => setTrackMode('multi')}
              disabled={uploading}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                trackMode === 'multi'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              複数トラック（コーチ + 参加者）
            </button>
          </div>

          {/* 隠し input */}
          <input
            ref={mixedInputRef}
            type="file"
            accept="audio/*,video/*,.wav,.mp3,.m4a,.flac,.mp4,.webm,.mov,.m4v"
            multiple
            onChange={(e) => {
              if (e.target.files) void addFiles(Array.from(e.target.files), setMixedFiles);
              e.target.value = '';
            }}
            className="hidden"
          />
          <input
            ref={tr1InputRef}
            type="file"
            accept="audio/*,video/*,.wav,.mp3,.m4a,.flac,.mp4,.webm,.mov,.m4v"
            multiple
            onChange={(e) => {
              if (e.target.files) void addFiles(Array.from(e.target.files), setTr1Files);
              e.target.value = '';
            }}
            className="hidden"
          />
          <input
            ref={trlrInputRef}
            type="file"
            accept="audio/*,video/*,.wav,.mp3,.m4a,.flac,.mp4,.webm,.mov,.m4v"
            multiple
            onChange={(e) => {
              if (e.target.files) void addFiles(Array.from(e.target.files), setTrlrFiles);
              e.target.value = '';
            }}
            className="hidden"
          />

          {trackMode === 'single' ? (
            /* ── 単一ファイル ── */
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleMixedDrop}
              onClick={() => mixedInputRef.current?.click()}
              className={`mt-3 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
                dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : mixedFiles.length > 0
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              <p className="text-3xl text-gray-400">📁</p>
              <p className="mt-2 text-sm text-gray-600">ここにファイルをドラッグ&ドロップ</p>
              <p className="text-xs text-gray-400">
                または クリックしてファイルを選択（複数可）
              </p>
              <p className="mt-1 text-xs text-gray-400">対応形式: WAV, MP3, M4A, FLAC, MP4</p>
            </div>
          ) : (
            /* ── 複数トラック ── */
            <div className="mt-3 space-y-3">
              <p className="text-xs text-gray-500">
                ZOOM H5 の個別トラックをアップロード。2GB で分割されたファイルも複数選択可能。サーバー側で自動結合 → LUFS 正規化 → リミックスします。
              </p>

              <TrackDropZone
                label="コーチマイク（Tr1 / DJI Mic Mini 等）"
                badge="Tr1"
                required={false}
                files={tr1Files}
                progressKeyPrefix="tr1"
                progressMap={uploadProgress}
                onAdd={() => tr1InputRef.current?.click()}
                onRemove={(i) => setTr1Files((prev) => prev.filter((_, j) => j !== i))}
                onClear={() => setTr1Files([])}
                disabled={uploading}
                accent="blue"
              />

              <TrackDropZone
                label="参加者卓上マイク（TrLR / AT9921 等）"
                badge="LR"
                required={true}
                files={trlrFiles}
                progressKeyPrefix="trlr"
                progressMap={uploadProgress}
                onAdd={() => trlrInputRef.current?.click()}
                onRemove={(i) => setTrlrFiles((prev) => prev.filter((_, j) => j !== i))}
                onClear={() => setTrlrFiles([])}
                disabled={uploading}
                accent="teal"
              />

              {/* 時間比較バー */}
              {(tr1Files.length > 0 || trlrFiles.length > 0) && (
                <div
                  className={`rounded-lg border px-4 py-3 text-xs ${
                    hasDurationMismatch
                      ? 'border-red-200 bg-red-50'
                      : tr1Files.length > 0 && trlrFiles.length > 0
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="font-semibold text-gray-700">トラック時間チェック</span>
                    {tr1Files.length > 0 && trlrFiles.length > 0 &&
                      (hasDurationMismatch ? (
                        <span className="font-bold text-red-600">
                          ⚠ 差分 {formatDuration(durationDiff)}
                        </span>
                      ) : (
                        <span className="font-bold text-green-600">✓ 一致</span>
                      ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-gray-500">Tr1（コーチ）: </span>
                      <span className="font-mono font-medium text-gray-800">
                        {tr1Files.length > 0 ? formatDuration(tr1Total) : '—'}
                      </span>
                      <span className="ml-1 text-gray-400">({tr1Files.length}ファイル)</span>
                    </div>
                    <div>
                      <span className="text-gray-500">TrLR（参加者卓上）: </span>
                      <span className="font-mono font-medium text-gray-800">
                        {trlrFiles.length > 0 ? formatDuration(trlrTotal) : '—'}
                      </span>
                      <span className="ml-1 text-gray-400">({trlrFiles.length}ファイル)</span>
                    </div>
                  </div>
                  {hasDurationMismatch && (
                    <p className="mt-2 text-red-600">
                      Tr1 と TrLR の合計時間が一致しません。ファイルの選択を確認してください。
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* single モード時の選択済みファイル一覧（duration 表示 + 削除） */}
          {trackMode === 'single' && mixedFiles.length > 0 && (
            <div className="mt-3 space-y-2">
              {mixedFiles.map((entry, i) => {
                const key = `mixed/${entry.file.name}`;
                const pct = uploadProgress[key];
                return (
                  <div
                    key={`${entry.file.name}-${i}`}
                    className="rounded-lg bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="text-lg">🎵</span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {entry.file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatSize(entry.file.size)} · {formatDuration(entry.duration)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMixedFiles((prev) => prev.filter((_, j) => j !== i))}
                        disabled={uploading}
                        className="text-xs text-red-500 hover:underline disabled:opacity-50"
                      >
                        削除
                      </button>
                    </div>
                    {pct !== undefined && (
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-gray-100">
                        <div
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-gray-400">{mixedFiles.length} ファイル選択済み</p>
            </div>
          )}
        </div>

        {/* ステータス・エラー表示 */}
        {statusMessage && (
          <div className="rounded-md bg-blue-50 px-4 py-2 text-sm text-blue-700">
            {statusMessage}
          </div>
        )}
        {shouldShowUploadProgress && (
          <UploadProgressPanel
            rows={uploadProgressRows}
            overallPercent={overallUploadProgress}
            completedCount={uploadCompletedCount}
          />
        )}
        {errorMessage && (
          <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{errorMessage}</div>
        )}
        {failedUploadRecoveryPanel}

        {/* 送信 */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit || uploading}
            className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            確認へ進む
          </button>
          <button
            type="button"
            onClick={() => navigate(`/clients/${clientId}`)}
            disabled={uploading}
            className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            キャンセル
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

function UploadProgressPanel({
  rows,
  overallPercent,
  completedCount,
}: {
  rows: Array<UploadTarget & { percent: number }>;
  overallPercent: number;
  completedCount: number;
}) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">アップロード進捗</p>
          <p className="mt-0.5 text-xs text-blue-700">
            {completedCount} / {rows.length} ファイル完了
          </p>
        </div>
        <span className="font-mono text-lg font-semibold text-blue-700">
          {overallPercent}%
        </span>
      </div>
      <div
        aria-label="総合アップロード進捗"
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={overallPercent}
        role="progressbar"
        className="mt-3 h-2 w-full overflow-hidden rounded bg-white"
      >
        <div
          className="h-full bg-blue-600 transition-all"
          style={{ width: `${overallPercent}%` }}
        />
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((row) => (
          <div key={row.key} className="rounded-md bg-white px-3 py-2">
            <div className="flex items-center justify-between gap-3 text-xs">
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-900">{row.filename}</p>
                <p className="text-gray-500">
                  {row.role} · {formatSize(row.size)}
                </p>
              </div>
              <span className="font-mono text-gray-700">{row.percent}%</span>
            </div>
            <div
              aria-label={`${row.filename} のアップロード進捗`}
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={row.percent}
              role="progressbar"
              className="mt-2 h-1.5 w-full overflow-hidden rounded bg-gray-100"
            >
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${row.percent}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfirmRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 border-b border-gray-100 px-4 py-3 text-sm last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
      <div className="font-medium text-gray-500">{label}</div>
      <div className="text-gray-900">{children}</div>
    </div>
  );
}

function FeedbackerSummaryList({
  feedbackers,
  loadFailed,
}: {
  feedbackers: ClientFeedbacker[] | null;
  loadFailed: boolean;
}) {
  if (loadFailed) return <span className="text-gray-500">取得できませんでした</span>;
  if (feedbackers === null) return <span className="text-gray-500">読み込み中...</span>;
  if (feedbackers.length === 0) {
    return (
      <span className="text-gray-500">
        未設定（このセッションのフィードバッカーは未設定になります）
      </span>
    );
  }

  return (
    <ul className="space-y-1">
      {feedbackers.map((feedbacker) => (
        <li key={feedbacker.user_id} className="min-w-0">
          <span className="font-medium">{feedbacker.user_name}</span>
          {feedbacker.email ? (
            <span className="ml-2 text-gray-500">{feedbacker.email}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function FileSummaryList({
  files,
  emptyLabel = '未選択',
}: {
  files: FileWithDuration[];
  emptyLabel?: string;
}) {
  if (files.length === 0) return <p className="text-gray-500">{emptyLabel}</p>;
  return (
    <ul className="space-y-1">
      {files.map((entry, i) => (
        <li key={`${entry.file.name}-${i}`} className="flex flex-wrap gap-x-2 gap-y-1">
          <span className="font-medium">{entry.file.name}</span>
          <span className="text-gray-500">
            {formatSize(entry.file.size)} / {formatDuration(entry.duration)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─── TrackDropZone: 役割別ファイル入力 ─────────────────────────
//
// PoC `frontend/src/pages/pipeline/UploadStep.tsx` の TrackDropZone を移植。
// ただし PoC は `border-${accentColor}-300` のような動的クラスを使っており
// Tailwind JIT の検出に引っかからないため、本実装では事前定義の static クラス
// オブジェクトを引く方式に変更している。

type Accent = 'blue' | 'teal';

const ACCENT_STYLES: Record<
  Accent,
  { borderActive: string; bgActive: string; badge: string; addBtn: string }
> = {
  blue: {
    borderActive: 'border-blue-300 bg-blue-50/50',
    bgActive: 'bg-blue-100 text-blue-700',
    badge: 'bg-blue-100 text-blue-700',
    addBtn: 'border-blue-300 text-blue-600 hover:bg-blue-50',
  },
  teal: {
    borderActive: 'border-teal-300 bg-teal-50/50',
    bgActive: 'bg-teal-100 text-teal-700',
    badge: 'bg-teal-100 text-teal-700',
    addBtn: 'border-teal-300 text-teal-600 hover:bg-teal-50',
  },
};

interface TrackDropZoneProps {
  label: string;
  badge: string;
  required: boolean;
  files: FileWithDuration[];
  progressKeyPrefix: string;
  progressMap: Record<string, number>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onClear: () => void;
  disabled: boolean;
  accent: Accent;
}

function TrackDropZone({
  label,
  badge,
  required,
  files,
  progressKeyPrefix,
  progressMap,
  onAdd,
  onRemove,
  onClear,
  disabled,
  accent,
}: TrackDropZoneProps) {
  const hasFiles = files.length > 0;
  const styles = ACCENT_STYLES[accent];
  const borderClass = hasFiles
    ? styles.borderActive
    : 'border-gray-300 hover:border-gray-400 bg-white';
  const badgeClass = hasFiles ? styles.badge : 'bg-gray-100 text-gray-400';
  const totalSize = (files.reduce((a, f) => a + f.file.size, 0) / (1024 * 1024)).toFixed(1);
  const totalDur = files.reduce((a, f) => a + f.duration, 0);

  return (
    <div className={`rounded-lg border-2 border-dashed p-4 transition-all ${borderClass}`}>
      <div className="mb-2 flex items-center gap-3">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${badgeClass}`}
        >
          {badge}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-700">
            {label}
            {required && <span className="ml-1 text-red-400">*</span>}
          </p>
          {hasFiles ? (
            <p className="text-xs text-gray-500">
              {files.length}ファイル ({totalSize} MB) / {formatDuration(totalDur)} - 表示順で結合
            </p>
          ) : (
            <p className="text-xs text-gray-400">{required ? '必須' : '任意'} - 複数選択可</p>
          )}
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className={`rounded border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
            hasFiles ? 'border-gray-300 text-gray-600 hover:bg-gray-100' : styles.addBtn
          }`}
        >
          + 追加
        </button>
        {hasFiles && (
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            className="text-xs text-gray-400 transition-colors hover:text-red-500 disabled:opacity-50"
          >
            全削除
          </button>
        )}
      </div>

      {hasFiles && (
        <div className="mt-2 space-y-1 pl-11">
          {files.map((fd, i) => {
            const key = `${progressKeyPrefix}/${fd.file.name}`;
            const pct = progressMap[key];
            return (
              <div key={`${fd.file.name}-${i}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-4 text-right font-mono text-gray-400">{i + 1}.</span>
                  <span className="flex-1 truncate text-gray-700">{fd.file.name}</span>
                  <span className="flex-shrink-0 font-mono text-gray-400">
                    {formatDuration(fd.duration)}
                  </span>
                  <span className="flex-shrink-0 text-gray-300">
                    {(fd.file.size / (1024 * 1024)).toFixed(0)} MB
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemove(i)}
                    disabled={disabled}
                    className="flex-shrink-0 text-gray-300 transition-colors hover:text-red-500 disabled:opacity-50"
                  >
                    ✕
                  </button>
                </div>
                {pct !== undefined && (
                  <div className="ml-4 mt-1 h-1 w-full overflow-hidden rounded bg-gray-100">
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
